import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import {
  createStockEntry,
  getEstabelecimentos,
  getLocaisEstoque,
} from '../services/api';
import { getAuthPermissoes } from '../services/authStorage';
import { getApiErrorMessage } from '../utils/apiError';
import useModulosLicenca from '../hooks/useModulosLicenca';
import { useEstabelecimentoFiltroApi } from '../context/EstabelecimentoContext';
import {
  processarBuscaProdutoEnter,
  PRODUCT_SEARCH_HINT,
} from '../utils/productSearch';
import PageHeader from './common/PageHeader';
import ResponsiveTable from './common/ResponsiveTable';

const num = (v) => {
  const n = Number(String(v ?? '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

const custoCadastro = (produto) => {
  const precos = produto?.precos || produto?.Precos || [];
  const row = precos.find((p) => p.tipo === 3 || p.tipo === 'Custo' || p.Tipo === 3);
  return Number(row?.valor ?? row?.Valor ?? produto?.custo ?? produto?.valorCusto ?? 0) || 0;
};

export default function EntradaEstoqueManualPage() {
  const navigate = useNavigate();
  const { isAtivo } = useModulosLicenca();
  const multiEstoque = isAtivo('ADD_MULTI_ESTOQUE');
  const estabelecimentoContextoId = useEstabelecimentoFiltroApi();
  const permissions = useMemo(() => getAuthPermissoes() || {}, []);
  const podeMovimentar = Boolean(permissions.movimentaEstoque);

  const [filiais, setFiliais] = useState([]);
  const [estabId, setEstabId] = useState(estabelecimentoContextoId ? String(estabelecimentoContextoId) : '');
  const [locais, setLocais] = useState([]);
  const [localId, setLocalId] = useState('');
  const [busca, setBusca] = useState('');
  const [escolha, setEscolha] = useState(null);
  const [itens, setItens] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getEstabelecimentos();
        const lista = Array.isArray(data) ? data : (data ? [data] : []);
        setFiliais(lista.map((e) => ({
          id: e.id,
          nome: e.nomeFantasia || e.razaoSocial || `Filial ${e.id}`,
        })));
        if (lista.length === 1) setEstabId(String(lista[0].id));
      } catch {
        setFiliais([]);
      }
    })();
  }, []);

  useEffect(() => {
    if (!estabelecimentoContextoId) return;
    setEstabId((prev) => prev || String(estabelecimentoContextoId));
  }, [estabelecimentoContextoId]);

  useEffect(() => {
    const estab = Number(estabId);
    if (!multiEstoque || !estab) {
      setLocais([]);
      setLocalId('');
      return undefined;
    }
    let ativo = true;
    getLocaisEstoque(estab)
      .then(({ data }) => {
        if (!ativo) return;
        const lista = (Array.isArray(data) ? data : []).filter((l) => l.ativo !== false);
        setLocais(lista);
        const principal = lista.find((l) => l.principal) || lista[0];
        setLocalId(principal ? String(principal.id) : '');
      })
      .catch(() => {
        if (!ativo) return;
        setLocais([]);
        setLocalId('');
      });
    return () => { ativo = false; };
  }, [estabId, multiEstoque]);

  const incluirProduto = (p, quantidade = 1) => {
    setItens((prev) => {
      const idx = prev.findIndex((i) => i.produtoId === p.id);
      if (idx >= 0) {
        return prev.map((i, k) => (
          k === idx ? { ...i, quantidade: num(i.quantidade) + Number(quantidade || 1) } : i
        ));
      }
      return [
        ...prev,
        {
          produtoId: p.id,
          codigo: p.codigo,
          descricao: p.descricao,
          quantidade: Number(quantidade || 1),
          custoUnitario: custoCadastro(p) || '',
          precoVarejo: '',
          observacao: '',
        },
      ];
    });
    setBusca('');
  };

  const adicionar = async () => {
    setError('');
    if (!estabId) {
      setError('Selecione a filial.');
      return;
    }
    try {
      const resultado = await processarBuscaProdutoEnter({
        termo: busca,
        estabelecimentoId: estabId,
        ativo: true,
      });
      if (resultado.tipo === 'vazio') return;
      if (resultado.tipo === 'nao_encontrado') {
        setError(resultado.mensagem || 'Produto não encontrado.');
        return;
      }
      if (resultado.tipo === 'lista') {
        setEscolha(resultado.produtos || []);
        return;
      }
      incluirProduto(resultado.produto, resultado.quantidade);
    } catch (e) {
      setError(getApiErrorMessage(e, 'Erro ao buscar produto.'));
    }
  };

  const setField = (idx, patch) =>
    setItens((prev) => prev.map((i, k) => (k === idx ? { ...i, ...patch } : i)));

  const salvar = async () => {
    setError('');
    setInfo('');
    if (!podeMovimentar) {
      setError('Sem permissão para movimentar estoque.');
      return;
    }
    if (!Number(estabId)) {
      setError('Selecione a filial.');
      return;
    }
    if (itens.length === 0) {
      setError('Inclua ao menos um item.');
      return;
    }
    if (itens.some((i) => num(i.quantidade) <= 0)) {
      setError('A quantidade deve ser maior que zero em todos os itens.');
      return;
    }
    if (itens.some((i) => num(i.custoUnitario) < 0 || num(i.precoVarejo) < 0)) {
      setError('Custo e preço de varejo não podem ser negativos.');
      return;
    }
    const payload = itens.map((i) => {
      const custo = num(i.custoUnitario);
      const varejo = num(i.precoVarejo);
      return {
        produtoId: i.produtoId,
        quantidade: num(i.quantidade),
        estabelecimentoId: Number(estabId),
        localEstoqueId: Number(localId) || 0,
        tipoId: 1,
        ...(custo > 0 ? { custoUnitario: custo, atualizarCusto: true } : { atualizarCusto: false }),
        ...(varejo > 0 ? { precoVarejo: varejo } : {}),
        ...(i.observacao ? { observacao: i.observacao } : {}),
      };
    });
    setSaving(true);
    try {
      const { data: res } = await createStockEntry(payload);
      const falhas = res?.errosDetalhe || [];
      if ((res?.erros || 0) > 0 || falhas.length > 0) {
        const falhouIds = falhas.map((f) => f.produtoId);
        // Mantém na tela só o que falhou, para reenviar sem duplicar o que já entrou.
        setItens((prev) => prev.filter((i) => falhouIds.includes(i.produtoId)));
        setError(
          `Entrada parcial. ${res?.criados ?? 0} lançado(s). Falhas: ${falhas.map((f) => f.mensagem).join('; ') || 'verifique os itens'}. `
          + 'Os itens já lançados foram removidos da lista.',
        );
        return;
      }
      setItens([]);
      setInfo(`Entrada registrada (${res?.criados ?? payload.length} item(ns)).`);
    } catch (e) {
      setError(getApiErrorMessage(e, 'Falha ao registrar entrada de estoque.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <PageHeader
        title="Entrada manual de estoque"
        subtitle="Lance a entrada de vários produtos de uma vez, com custo e preço de varejo opcionais."
        actions={(
          <Button variant="outlined" onClick={() => navigate('/stock-movement')}>
            Movimentos
          </Button>
        )}
      />

      {!podeMovimentar && (
        <Alert severity="warning" sx={{ mb: 2 }}>Sem permissão para movimentar estoque.</Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')} style={{ whiteSpace: 'pre-line' }}>
          {error}
        </Alert>
      )}
      {info && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo('')}>{info}</Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          {filiais.length > 0 && (
            <TextField
              select
              required
              size="small"
              label="Filial"
              value={estabId}
              onChange={(e) => setEstabId(e.target.value)}
              sx={{ minWidth: 220 }}
            >
              {filiais.map((f) => (
                <MenuItem key={f.id} value={String(f.id)}>{f.nome}</MenuItem>
              ))}
            </TextField>
          )}
          {multiEstoque && locais.length > 0 && (
            <TextField
              select
              required
              size="small"
              label="Local de estoque"
              value={localId}
              onChange={(e) => setLocalId(e.target.value)}
              sx={{ minWidth: 220 }}
            >
              {locais.map((l) => (
                <MenuItem key={l.id} value={String(l.id)}>
                  {l.nome}{l.principal ? ' (principal)' : ''}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mb: 1 }}>
          <TextField
            label="Produto"
            size="small"
            fullWidth
            value={busca}
            helperText={PRODUCT_SEARCH_HINT}
            onChange={(e) => setBusca(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                adicionar();
              }
            }}
          />
          <Button variant="contained" onClick={adicionar}>Incluir</Button>
        </Stack>

        <ResponsiveTable>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Produto</TableCell>
                <TableCell align="right">Quantidade</TableCell>
                <TableCell align="right">Custo unit.</TableCell>
                <TableCell align="right">Preço varejo</TableCell>
                <TableCell>Observação</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {itens.map((it, idx) => (
                <TableRow key={it.produtoId}>
                  <TableCell data-label="Produto">
                    <Typography variant="body2">{it.descricao}</Typography>
                    <Typography variant="caption" color="text.secondary">{it.codigo || '-'}</Typography>
                  </TableCell>
                  <TableCell data-label="Quantidade" align="right">
                    <TextField
                      size="small"
                      type="number"
                      value={it.quantidade}
                      onChange={(e) => setField(idx, { quantidade: e.target.value })}
                      inputProps={{ min: 0, step: 'any' }}
                      sx={{ width: 100 }}
                    />
                  </TableCell>
                  <TableCell data-label="Custo unit." align="right">
                    <TextField
                      size="small"
                      type="number"
                      value={it.custoUnitario}
                      onChange={(e) => setField(idx, { custoUnitario: e.target.value })}
                      inputProps={{ min: 0, step: 'any' }}
                      sx={{ width: 110 }}
                    />
                  </TableCell>
                  <TableCell data-label="Preço varejo" align="right">
                    <TextField
                      size="small"
                      type="number"
                      placeholder="—"
                      value={it.precoVarejo}
                      onChange={(e) => setField(idx, { precoVarejo: e.target.value })}
                      inputProps={{ min: 0, step: 'any' }}
                      sx={{ width: 110 }}
                    />
                  </TableCell>
                  <TableCell data-label="Observação">
                    <TextField
                      size="small"
                      value={it.observacao}
                      onChange={(e) => setField(idx, { observacao: e.target.value })}
                      inputProps={{ maxLength: 500 }}
                      sx={{ minWidth: 160 }}
                    />
                  </TableCell>
                  <TableCell data-actions="" align="right">
                    <Tooltip title="Remover">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setItens((p) => p.filter((_, k) => k !== idx))}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {itens.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography color="text.secondary" align="center" sx={{ py: 2 }}>
                      Nenhum item.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </ResponsiveTable>
      </Paper>

      <Stack direction="row" spacing={1}>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={saving || !podeMovimentar || itens.length === 0}
          onClick={salvar}
        >
          {saving ? 'Gravando…' : 'Gravar entrada'}
        </Button>
        <Button variant="outlined" disabled={saving || itens.length === 0} onClick={() => setItens([])}>
          Limpar lista
        </Button>
      </Stack>

      <Dialog open={Boolean(escolha)} onClose={() => setEscolha(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Vários produtos encontrados</DialogTitle>
        <DialogContent dividers>
          <List dense>
            {(escolha || []).map((p) => (
              <ListItemButton
                key={p.id}
                onClick={() => {
                  incluirProduto(p, 1);
                  setEscolha(null);
                }}
              >
                <ListItemText
                  primary={p.descricao}
                  secondary={[p.codigo, p.ean].filter(Boolean).join(' · ')}
                />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEscolha(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
